import { DISPATCH_TARGET, DispatchTarget } from '../../../../../shared/constants/dispatchTarget';
import * as C from '../../../../ui/common/CommonStyles';
import { Button, Divider, Group, Space, Text, TextInput } from '@mantine/core';
import { IconPlus, IconX } from '@tabler/icons-react';
import CompanySearchModal from '../Modal/CompanySearchModal';
import { useDisclosure } from '@mantine/hooks';
import DeviceListModal from '../Modal/DeviceListModal';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import CompanyHistoryModal from '../Modal/CompanyHistoryModal';
import Swal from 'sweetalert2';
import { showSuccessNotification } from '../../../../ui/notification';
import { AxiosError } from 'axios';
import { AxiosCommonError, ICompanyData } from '../../../../../shared/types';
import { handleError } from '../../../../ui/err/handleError';
import { useDeviceDetails } from './hooks/useDeviceDetails';
import { useRegisterMutation } from './hooks/useRegisterMutation';
import CompanySection from './sections/companySection';
import DeviceInfoSection from './sections/deviceInfoSection';
import { IDevice } from './types';
import { useRows } from './hooks/useRows';
import { useCheckbox } from '../../../../../shared/hook/useCheckbox';

interface IDispatchRegisterProps {
  target: DispatchTarget;
}

const EDGECROSS_COMPANY = { id: 1, name: '주식회사 엣지크로스' };

export default function DispatchRegister(props: IDispatchRegisterProps) {
  const { target } = props;
  const memoRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [companyType, setCompanyType] = useState<'from' | 'to'>('to');
  const [fromCompany, setFromCompany] = useState<Partial<ICompanyData> | null>(null);
  const [toCompany, setToCompany] = useState<Partial<ICompanyData> | null>(null);
  const { rows, onUpdateRows, onRemoveRow } = useRows();

  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id') || '';
  const location = useLocation();
  const isEptp = location.pathname.includes('eptp');

  const { dispatchedDeviceDetail } = useDeviceDetails(editId);

  const [openedCompanySearchModal, { open: openCompanySearchModal, close: closeCompanySearchModal }] = useDisclosure(false);
  const [openedDeviceListModal, { open: openDeviceListModal, close: closeDeviceListModal }] = useDisclosure(false);
  const [openedCompanyHistoryModal, { open: openCompanyHistoryModal, close: closeCompanyHistoryModal }] = useDisclosure(false);

  const { postDispatchDevices, putDispatchDevices } = useRegisterMutation();

  const updateCompany = (type: 'from' | 'to', company: Partial<ICompanyData>) => {
    if (type === 'from') {
      setFromCompany({ id: company.id, name: company.name });
    } else {
      setToCompany({ id: company.id, name: company.name });
    }
  };

  const openCompanySearch = (type: 'from' | 'to') => {
    setCompanyType(type);
    openCompanySearchModal();
  };

  const onClickCancel = () => {
    Swal.fire({
      title: '작성 취소',
      text: '작성 중인 내용이 사라집니다. 취소하시겠습니까?',
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: '취소',
      confirmButtonText: '확인',
    }).then((result) => {
      if (result.isConfirmed) {
        navigate(-1);
      }
    });
  };

  const onSubmit = () => {
    if (!fromCompany || !toCompany || !fromCompany.id || !toCompany.id) {
      Swal.fire({
        icon: 'warning',
        title: '거래처를 선택해주세요.',
        confirmButtonText: '확인',
      });
      return;
    }

    if (editId) {
      putDispatchDevices(
        {
          id: Number(editId),
          fromCompanyId: fromCompany?.id,
          toCompanyId: toCompany?.id,
          memo: memoRef.current?.value,
        },
        {
          onSuccess: () => {
            showSuccessNotification('변경되었습니다.');
            navigate(-1);
          },
          onError: (error: unknown) => {
            handleError(error as AxiosError<AxiosCommonError>);
          },
        },
      );
      return;
    }

    if (rows.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: '출고할 디바이스를 선택해주세요.',
        confirmButtonText: '확인',
      });
      return;
    }

    postDispatchDevices(
      {
        fromCompanyId: fromCompany?.id,
        toCompanyId: toCompany?.id,
        memo: memoRef.current?.value,
        channel: isEptp ? 'MFM' : 'SCAUTR',
        deviceIds: Array.from(rows.map((r) => r.id)),
      },
      {
        onSuccess: () => {
          showSuccessNotification('등록되었습니다.');
          navigate(-1);
        },
        onError: (error: unknown) => {
          handleError(error as AxiosError<AxiosCommonError>);
        },
      },
    );
  };

  const onClear = () => {
    if (isEptp) {
      setFromCompany(EDGECROSS_COMPANY);
    } else {
      setFromCompany(null);
    }
    setToCompany(null);
    if (memoRef.current) {
      memoRef.current.value = '';
    }
    onUpdateRows([]);
  };

  useEffect(() => {
    if (dispatchedDeviceDetail) {
      setFromCompany({
        id: dispatchedDeviceDetail.fromCompanyId,
        name: dispatchedDeviceDetail.fromCompanyName,
      });
      setToCompany({
        id: dispatchedDeviceDetail.toCompanyId,
        name: dispatchedDeviceDetail.toCompanyName,
      });
      if (memoRef.current) {
        memoRef.current.value = dispatchedDeviceDetail.memo || '';
      }
    }
  }, [dispatchedDeviceDetail]);

  useEffect(() => {
    if (isEptp) {
      setFromCompany(EDGECROSS_COMPANY);
    }
  }, [isEptp]);

  // 디바이스 정보 입력 테이블의 체크박스 상태
  const deviceCheckbox = useCheckbox();

  const updateDeviceRowsAndCheckbox = (newRows: IDevice[]) => {
    onUpdateRows(newRows);
    deviceCheckbox.setSelectedIds(new Set());
  };

  const handleOpenDeviceListModal = () => {
    if (!fromCompany) {
      Swal.fire({
        icon: 'warning',
        title: '출고 거래처를 먼저 선택해주세요.',
        confirmButtonText: '확인',
      });
      return;
    }
    openDeviceListModal();
  };
  return (
    <div>
      <C.TitleBox>
        <div className="title">
          {`출고 ${editId ? '수정' : '등록'}`}({DISPATCH_TARGET[target.value].label})
        </div>
      </C.TitleBox>
      <C.ListBox>
        <CompanySection
          editId={editId ? Number(editId) : null}
          openCompanySearch={openCompanySearch}
          openCompanyHistoryModal={openCompanyHistoryModal}
          fromCompany={fromCompany as ICompanyData | null}
          toCompany={toCompany as ICompanyData | null}
          isEptp={isEptp}
          isRegistered={dispatchedDeviceDetail?.registrationStatus === 'REGISTERED' || false}
          onClear={onClear}
        />
        <Space h="lg" />
        <div>
          <Text fw="bold">메모</Text>
          <Divider my="xs" />
          <TextInput ref={memoRef} label="출고 관련 메모" placeholder="메모를 입력하세요." />
        </div>
        <Space h="lg" />
        <DeviceInfoSection
          editId={editId ? Number(editId) : null}
          openDeviceListModal={handleOpenDeviceListModal}
          rows={rows}
          onRemoveRow={onRemoveRow}
          dispatchedDeviceDetail={dispatchedDeviceDetail}
          deviceCheckbox={deviceCheckbox}
          isReady={fromCompany !== null && toCompany !== null}
        />
        <Space h="lg" />
        <Group justify="center" mt="xl">
          <Button variant="outline" leftSection={<IconX size={16} />} onClick={onClickCancel}>
            취소
          </Button>
          <Button color="secondary.5" leftSection={<IconPlus size={16} />} onClick={onSubmit}>
            저장
          </Button>
        </Group>
      </C.ListBox>
      {openedCompanySearchModal && (
        <CompanySearchModal
          opened={openedCompanySearchModal}
          close={closeCompanySearchModal}
          updateCompany={updateCompany}
          companyType={companyType}
        />
      )}
      {openedDeviceListModal && (
        <DeviceListModal
          opened={openedDeviceListModal}
          close={closeDeviceListModal}
          initialRows={rows}
          onConfirm={updateDeviceRowsAndCheckbox}
          fromCompanyId={fromCompany?.id || null}
        />
      )}
      {openedCompanyHistoryModal && (
        <CompanyHistoryModal
          opened={openedCompanyHistoryModal}
          close={closeCompanyHistoryModal}
          mac={dispatchedDeviceDetail?.mac || ''}
        />
      )}
    </div>
  );
}
